import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { qualityAPI, Concession, ConcessionDashboardStats, ConcessionDeviationType, ConcessionRiskLevel } from '../../lib/api';
import { usePagination } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useIsSupplier, useIsCustomer } from '../../hooks/usePortalMode';
import { useView } from '../../contexts/ViewContext';
import { FIELD_MAPPING } from './constants';

export interface NewConcessionForm {
  customer: string;
  part_number: string;
  part_description: string;
  quantity_affected: string;
  deviation_type: 'dimensional' | 'cosmetic' | 'material' | 'process' | 'documentation' | 'packaging' | 'other';
  title: string;
  description: string;
  reason: string;
  proposed_disposition: string;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  risk_justification: string;
}

const INITIAL_FORM: NewConcessionForm = {
  customer: '',
  part_number: '',
  part_description: '',
  quantity_affected: '',
  deviation_type: 'dimensional',
  title: '',
  description: '',
  reason: '',
  proposed_disposition: '',
  risk_level: 'medium',
  risk_justification: '',
};

export interface ConcessionFiltersState {
  status: string;
  riskLevel: string;
  deviationType: string;
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_CONCESSION_FILTERS: ConcessionFiltersState = {
  status: '',
  riskLevel: '',
  deviationType: '',
  dateFrom: '',
  dateTo: '',
};

export function useConcessions() {
  const navigate = useNavigate();
  const isSupplier = useIsSupplier();
  const isCustomer = useIsCustomer();
  const { viewKey, getViewQueryParams } = useView();
  const [stats, setStats] = useState<ConcessionDashboardStats | null>(null);
  const [concessions, setConcessions] = useState<Concession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ConcessionFiltersState>(EMPTY_CONCESSION_FILTERS);
  const [sortField, setSortField] = useState<string>('requested_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newConcession, setNewConcession] = useState<NewConcessionForm>(INITIAL_FORM);

  const pagination = usePagination({
    pageSize: 15,
    onPageChange: () => {},
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    pagination.reset();
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const backendField = FIELD_MAPPING[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;

      const viewParams = getViewQueryParams();
      const statsParams: Record<string, string | number> = {};
      if (viewParams['site']) statsParams['site'] = viewParams['site'];
      if (viewParams['company']) statsParams['company'] = viewParams['company'];
      if (viewParams['group']) statsParams['group'] = viewParams['group'];
      const concessionParams: Record<string, string | number | undefined> = {
        ordering,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        search: searchTerm || undefined,
        ...viewParams,
      };
      if (filters.status) concessionParams['status'] = filters.status;
      if (filters.riskLevel) concessionParams['risk_level'] = filters.riskLevel;
      if (filters.deviationType) concessionParams['deviation_type'] = filters.deviationType;
      if (filters.dateFrom) concessionParams['requested_date__gte'] = filters.dateFrom;
      if (filters.dateTo) concessionParams['requested_date__lte'] = filters.dateTo;

      const [statsResponse, concessionsResponse] = await Promise.all([
        qualityAPI.getConcessionStats(statsParams),
        qualityAPI.getConcessions(concessionParams),
      ]);

      setStats(statsResponse.data);
      setConcessions(concessionsResponse.data.results);
      pagination.setFromResponse(concessionsResponse.data.count);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load concessions data'));
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, searchTerm, filters, sortField, sortDirection, isSupplier, viewKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewConcession = (concessionId: number) => {
    navigate(`/quality/concession/${concessionId}`);
  };

  const handleCreateConcession = async () => {
    try {
      setCreateLoading(true);
      await qualityAPI.createConcession({
        customer: parseInt(newConcession.customer),
        part_number: newConcession.part_number,
        part_description: newConcession.part_description,
        quantity_affected: parseInt(newConcession.quantity_affected),
        deviation_type: newConcession.deviation_type as ConcessionDeviationType,
        title: newConcession.title,
        description: newConcession.description,
        reason: newConcession.reason,
        proposed_disposition: newConcession.proposed_disposition,
        risk_level: newConcession.risk_level as ConcessionRiskLevel,
        risk_justification: newConcession.risk_justification,
      });
      setShowCreateDialog(false);
      setNewConcession(INITIAL_FORM);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create concession'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    pagination.reset();
  };

  const handleFiltersChange = (next: ConcessionFiltersState) => {
    setFilters(next);
    pagination.reset();
  };

  return {
    // Navigation
    navigate,

    // User info
    isSupplier,
    isCustomer,

    // Loading states
    loading,
    error,
    createLoading,

    // Data
    stats,
    concessions,
    searchTerm,
    filters,
    sortField,
    sortDirection,
    pagination,

    // Create dialog
    showCreateDialog,
    setShowCreateDialog,
    newConcession,
    setNewConcession,

    // Actions
    fetchData,
    handleSort,
    handleViewConcession,
    handleCreateConcession,
    handleSearchChange,
    handleFiltersChange,
  };
}
